<template>
  <div
    class="tab-container"
    :class="{'tab-container-fake-size': !hasItems && isLoadingComments}"
  >
    <PDPFilter
      v-if="!forDiscussionPage"
      class="mb-3"
      :comment-type-title-lc="commentTypeTitleLc"
      :show-filter="showFilter"
      :is-authenticated="user.isAuthenticated"
      :is-user-verified="!!user.isEmailVerified"
      :has-purchased="hasPurchased"
      :show-all="showAllChildren"
      :allow-create-item="allowCreateItem"
      :has-items="hasItems"
      :num-visible-comments="comments.length"
      :for-reviews="forReviews"
      :is-moderator-view="isModeratorView"
      @on-click-refresh="onClickRefresh"
      @toggle-comment="toggleComment"
      @toggle-show-all="toggleShowAll"
      @on-change-filter="onChangeFilter"
    />
    <slot v-if="hasItems">
      <div
        v-for="(comment, index) in comments"
        :key="`${index}-review-${comment.comment}`"
        class="comment-container"
      >
        <PDPComment
          :comment-type-title="commentTypeTitle"
          :allow-flagging="allowFlagging"
          :comment="comment"
          :comment-link="commentLink"
          :force-show-all="forceShowAll"
          :deal-partner-user-ids="dealPartnerUserIds"
          :filter-comments="filterComments"
          :position-indices="[index]"
          :show-all-children="showAllChildren"
          :user="user"
          :for-reviews="forReviews"
          @click-flag="onClickFlag"
          @reply-comment="replyComment"
          @update-action="updateAction"
          @on-click-clear="onClickClear"
        />
      </div>
    </slot>
    <PDPEmptyStateCard
      v-else-if="!isLoadingComments"
      :title="emptyStateTitle"
      :description="emptyStateDescription"
      :button-text="`Write a ${commentTypeTitleLc}`"
      :button-disabled="!allowCreateItem || !user.isEmailVerified"
      @btn-clicked="toggleComment"
    />

    <Loading
      v-if="isLoadingComments"
      :message="loadingMessage"
    />
  </div>
</template>

<script>
import Loading from '../../ui/Loading.vue';
import PDPFilter from './PDPFilter.vue';
import PDPComment from '../comment/PDPComment.vue';
import PDPEmptyStateCard from '../PDPEmptyStateCard.vue';

export default {
  name: 'PDPDiscussion',
  components: {
    Loading,
    PDPComment,
    PDPFilter,
    PDPEmptyStateCard,
  },
  inject: {
    forDiscussionPage: {
      default: false,
    },
  },
  props: {
    allowFlagging: {
      type: Boolean,
    },
    commentLink: {
      type: Object,
      default: () => ({}),
    },
    comments: {
      type: Array,
      default: () => ([]),
    },
    dealName: {
      type: String,
      required: true,
    },
    dealPartnerUserIds: {
      type: Array,
      required: true,
    },
    isLoadingComments: {
      type: Boolean,
    },
    hasPurchased: {
      type: Boolean,
    },
    filterComments: {
      type: Boolean,
    },
    reviewCount: {
      type: Number,
      default: 0,
    },
    forceShowAll: {
      type: Boolean,
    },
    showAllChildren: {
      type: Boolean,
      required: true,
    },
    unapprovedComments: {
      type: Object,
      default: () => ({}),
    },
    unresolvedComments: {
      type: Object,
      default: () => ({}),
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    hasItems: {
      type: Boolean,
      required: true,
    },
    commentTypeTitle: {
      type: String,
      required: true,
    },
    commentTypeTitleLc: {
      type: String,
      required: true,
    },
    forQuestions: {
      type: Boolean,
      required: true,
    },
    forReviews: {
      type: Boolean,
      required: true,
    },
    isModeratorView: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    isSearching() {
      return !!this.$store.getters['discussion/searchTerm'];
    },
    showFilter() {
      return (this.filterComments && this.user?.isModerator) || this.user?.isStaff;
    },
    emptyStateTitle() {
      if (this.forQuestions) {
        return this.isSearching
          ? 'A question so good, it hasn\'t been asked... yet'
          : 'No one has asked a question... yet';
      }

      return this.isSearching
        ? 'Hmm, no one has mentioned that... yet'
        : 'No one has reviewed this product... yet';
    },
    emptyStateDescription() {
      if (this.forQuestions) {
        return this.isSearching
          ? `Ask the ${this.dealName} team now to get answers.`
          : `Be the first to ask a question. Get a reply from the ${this.dealName} team.`;
      }

      return this.isSearching
        ? 'Write a review now. (Purchase required.)'
        : 'Be the first to write a review. (Purchase required.)';
    },
    allowCreateItem() {
      if (!this.user?.isAuthenticated) {
        return false;
      }

      return this.forQuestions || (this.forReviews && this.hasPurchased);
    },

    loadingMessage() {
      return `Just a moment, loading ${this.commentTypeTitleLc}s...`;
    },
  },
  methods: {
    onClickFlag(value) {
      this.$emit('click-flag', value);
    },
    onClickRefresh() {
      this.$emit('on-click-refresh', { resetHash: false });
    },
    onClickClear() {
      this.$emit('on-click-refresh', { resetHash: true });
    },
    onChangeFilter(data) {
      this.$emit('on-change-filter', data);
    },
    replyComment(data) {
      this.$emit('reply-comment', data);
    },
    toggleComment(data) {
      this.$emit('toggle-comment', data);
    },
    toggleShowAll() {
      this.$emit('toggle-show-all-children');
    },
    updateAction(value) {
      this.$emit('update-action', value);
    },
  },
};
</script>

<style lang="scss" scoped>
  // Make section to have enough scroll size so we can scroll without having shift layouts
  .tab-container-fake-size {
    min-height: 2000px;
  }

  .comment-container {
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
</style>
